import { FC, useMemo, useState } from 'react'
import { Flex, Switch, Typography, Popover } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Bar, ComposedChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts'
import { formatCurrencyLargeCompact, formatCurrency } from 'uikit/utils/number-formatters'
import type { NumberFormatOptions } from 'uikit/utils/number-formatters'
import { useCurrency } from 'contexts/CurrencyContext'
import { Table, TableProvider } from 'uikit/tables/default'
import { buildYearlyBreakdown, expandedRowColumns } from 'containers/STRTablePage/STRExpandedRow'
import { LegendDot } from '../components'
import { useHostReviewChartQuery } from './useHostReviewChartQuery'
import { ESTIMATED_REVENUE_MIN_MULTIPLIER, ESTIMATED_REVENUE_MAX_MULTIPLIER } from '../utils'
import type { STREntityResponse } from '../types'

const CHART_COLORS = {
  reported: '#2436BA',
  estimatedMin: '#03A9F4',
  estimatedDelta: '#B3E5FC',
  reviews: '#AA3EE4',
}

const ESTIMATED_RANGE_TOOLTIP = 'The estimated revenue calculated by IVIX’s model'

const BAR_CORNER_RADIUS = 4

const buildTopRoundedRectPath = (x: number, y: number, width: number, height: number, radius: number): string => {
  if (width <= 0 || height <= 0) return ''
  const r = Math.min(radius, width / 2, height)
  return [
    `M${x},${y + height}`,
    `L${x},${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `L${x + width - r},${y}`,
    `Q${x + width},${y} ${x + width},${y + r}`,
    `L${x + width},${y + height}`,
    'Z',
  ].join(' ')
}

interface EstimatedRangeShapeProps {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: { estimatedMin?: number | null; estimatedMax?: number | null }
}

const EstimatedRangeShape: FC<EstimatedRangeShapeProps> = ({ x = 0, y = 0, width = 0, height = 0, payload }) => {
  const max = payload?.estimatedMax ?? 0
  const min = payload?.estimatedMin ?? 0
  if (max <= 0 || width <= 0 || height <= 0) return null
  const baseline = y + height
  const minHeight = height * (min / max)
  const minY = baseline - minHeight
  return (
    <g>
      <path d={buildTopRoundedRectPath(x, y, width, height, BAR_CORNER_RADIUS)} fill={CHART_COLORS.estimatedDelta} />
      {minHeight > 0 && (
        <path
          d={buildTopRoundedRectPath(x, minY, width, minHeight, BAR_CORNER_RADIUS)}
          fill={CHART_COLORS.estimatedMin}
        />
      )}
    </g>
  )
}

const CHART_CONTAINER_STYLE: React.CSSProperties = {
  border: '1px solid #F0F0F0',
  borderRadius: 2,
  padding: 16,
  backgroundColor: '#fff',
}

const GRID_STROKE = '#f0f0f0'
const AXIS_TICK_STYLE = { fontSize: 12, fontFamily: 'Source Sans Pro' }
const AXIS_LABEL_STYLE = { fontSize: 14, fontFamily: 'Roboto', fontWeight: 400, letterSpacing: '0.25px', fill: '#000' }
const REVIEWS_LABEL_STYLE = { ...AXIS_LABEL_STYLE, fill: CHART_COLORS.reviews }
const REVIEWS_TICK_STYLE = { ...AXIS_TICK_STYLE, fill: CHART_COLORS.reviews }

export interface FinancialsProps {
  totalRevenue: number
  unreportedRevenue: number
  overviewChartData: { year: string; reported: number | null; estimated: number | null; expenses: number | null }[]
  entityId?: string
  activityIndex?: string
  byYear?: STREntityResponse['totals']['byYear']
  reportedStatus?: STREntityResponse['entityDetails']['reportedStatus']
}

const RevenueSummary: FC<{ totalRevenue: number; unreportedRevenue: number; currencyOpts: NumberFormatOptions }> = ({
  totalRevenue,
  unreportedRevenue,
  currencyOpts,
}) => {
  const estimatedMin = formatCurrencyLargeCompact(totalRevenue * ESTIMATED_REVENUE_MIN_MULTIPLIER, currencyOpts)
  const estimatedMax = formatCurrencyLargeCompact(totalRevenue * ESTIMATED_REVENUE_MAX_MULTIPLIER, currencyOpts)
  return (
    <span>
      Total Estimated Revenue:{' '}
      <strong style={{ fontWeight: 500 }}>
        {estimatedMin} - {estimatedMax}{' '}
      </strong>
      | Unreported Revenue:{' '}
      <strong style={{ fontWeight: 500 }}> {formatCurrencyLargeCompact(unreportedRevenue, currencyOpts)}</strong>
    </span>
  )
}

interface ChartTooltipPayload {
  payload: {
    year: string
    reported: number | null
    estimatedMin: number | null
    estimatedMax: number | null
    reviews: number | null
  }
}

interface ChartTooltipProps {
  active?: boolean
  label?: string
  payload?: ChartTooltipPayload[]
  currencyOpts: NumberFormatOptions
  showReviews: boolean
}

const ChartTooltipContent: FC<ChartTooltipProps> = ({ active, label, payload, currencyOpts, showReviews }) => {
  if (!active || !payload?.length) return null
  const data = payload[0]?.payload
  if (!data) return null

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
        padding: '8px 12px',
        fontFamily: 'Source Sans Pro',
        fontSize: 13,
        lineHeight: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ marginBottom: 4, fontWeight: 600 }}>{label}</div>
      {data.reported != null && (
        <div>
          <span style={{ color: CHART_COLORS.reported, marginRight: 6 }}>●</span>
          Reported: {formatCurrency(data.reported, currencyOpts)}
        </div>
      )}
      {data.estimatedMin != null && data.estimatedMax != null && (
        <div>
          <span style={{ color: CHART_COLORS.estimatedMin, marginRight: 6 }}>●</span>
          Estimated Range: {formatCurrency(data.estimatedMin, currencyOpts)} -{' '}
          {formatCurrency(data.estimatedMax, currencyOpts)}
        </div>
      )}
      {showReviews && data.reviews != null && (
        <div>
          <span style={{ color: CHART_COLORS.reviews, marginRight: 6 }}>●</span>
          Reviews: {data.reviews}
        </div>
      )}
    </div>
  )
}

const RevenueChart: FC<{
  overviewChartData: FinancialsProps['overviewChartData']
  entityId?: string
  activityIndex?: string
  currencyOpts: NumberFormatOptions
}> = ({ overviewChartData, entityId, activityIndex, currencyOpts }) => {
  const [showReviews, setShowReviews] = useState(false)

  const { reviewChartData } = useHostReviewChartQuery({
    entityId,
    activityIndex: activityIndex ?? '',
  })

  const chartData = useMemo(() => {
    const reviewsByYear = new Map(reviewChartData.map(d => [d.year, d.reviews]))
    return overviewChartData.map(d => ({
      ...d,
      reviews: reviewsByYear.get(d.year) ?? null,
      estimatedMin: d.estimated != null ? d.estimated * ESTIMATED_REVENUE_MIN_MULTIPLIER : null,
      estimatedMax: d.estimated != null ? d.estimated * ESTIMATED_REVENUE_MAX_MULTIPLIER : null,
    }))
  }, [overviewChartData, reviewChartData])

  return (
    <div style={CHART_CONTAINER_STYLE}>
      <Flex justify='space-between' align='center' style={{ marginBottom: 12 }}>
        <Flex gap={16} align='center'>
          <LegendDot color={CHART_COLORS.reported} label='Reported' />
          <Flex align='center' gap={6}>
            <LegendDot color={CHART_COLORS.estimatedMin} label='Estimated Range' />
            <Popover
              mouseLeaveDelay={0.3}
              placement='top'
              styles={{ body: { padding: 0 } }}
              content={
                <div style={{ maxWidth: 260 }}>
                  <div
                    style={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: '24px',
                      color: 'rgba(0, 0, 0, 0.85)',
                      padding: '5px 16px',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    Estimated Revenue
                  </div>
                  <div
                    style={{
                      fontFamily: 'Roboto',
                      fontSize: 14,
                      lineHeight: '22px',
                      color: 'rgba(0, 0, 0, 0.85)',
                      padding: '12px 16px',
                    }}
                  >
                    {ESTIMATED_RANGE_TOOLTIP}
                  </div>
                </div>
              }
            >
              <InfoCircleOutlined style={{ color: '#B8AFAF', fontSize: 14, cursor: 'help' }} />
            </Popover>
          </Flex>
        </Flex>

        <Switch
          checked={showReviews}
          onChange={setShowReviews}
          checkedChildren='Reviews'
          unCheckedChildren='Reviews'
          style={{ backgroundColor: showReviews ? CHART_COLORS.reviews : undefined }}
        />
      </Flex>

      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart data={chartData} barGap={4} barCategoryGap={8}>
            <CartesianGrid vertical={false} stroke={GRID_STROKE} />
            <XAxis
              dataKey='year'
              tickLine={false}
              axisLine={{ stroke: GRID_STROKE }}
              tick={{ fontSize: 13, fontFamily: 'Source Sans Pro' }}
            />
            <YAxis
              yAxisId='financial'
              tickLine={false}
              axisLine={false}
              tick={AXIS_TICK_STYLE}
              tickFormatter={(v: number) => formatCurrencyLargeCompact(v, currencyOpts) as string}
              label={{ value: 'revenue', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }}
            />
            {showReviews && (
              <YAxis
                yAxisId='reviews'
                orientation='right'
                tickLine={false}
                axisLine={false}
                tick={REVIEWS_TICK_STYLE}
                allowDecimals={false}
                label={{ value: 'reviews', angle: -90, position: 'insideRight', style: REVIEWS_LABEL_STYLE }}
              />
            )}
            <Tooltip
              cursor={false}
              content={
                (
                  <ChartTooltipContent currencyOpts={currencyOpts} showReviews={showReviews} />
                ) as unknown as React.ReactElement
              }
            />
            <Legend wrapperStyle={{ display: 'none' }} />
            <Bar
              yAxisId='financial'
              dataKey='reported'
              fill={CHART_COLORS.reported}
              radius={[4, 4, 0, 0]}
              barSize={18}
              name='Reported'
            />
            <Bar
              yAxisId='financial'
              dataKey='estimatedMax'
              barSize={18}
              name='Estimated Range'
              shape={<EstimatedRangeShape />}
            />
            {showReviews && (
              <Line
                yAxisId='reviews'
                type='monotone'
                dataKey='reviews'
                stroke={CHART_COLORS.reviews}
                strokeWidth={2}
                dot={{ r: 4, fill: CHART_COLORS.reviews }}
                activeDot={{ r: 6 }}
                name='Reviews'
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

const YearlyBreakdownTable: FC<{
  entityId?: string
  byYear: STREntityResponse['totals']['byYear']
  reportedStatus?: STREntityResponse['entityDetails']['reportedStatus']
}> = ({ entityId, byYear, reportedStatus }) => (
  <TableProvider columns={expandedRowColumns}>
    <Table
      dataSource={buildYearlyBreakdown({
        id: entityId,
        totals: { byYear },
        entityDetails: { reportedStatus },
      })}
      rowKey='id'
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  </TableProvider>
)

const Disclaimer: FC = () => {
  return (
    <Typography.Paragraph
      style={{
        color: '#C0C5D0',
        fontFamily: 'Roboto',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '22px',
      }}
    >
      Financial status reflects IVIX estimated revenue compared against taxpayer filing data provided by the authority,
      resolved per applicable tax rules for this jurisdiction. For detailed methodology, please refer to the Playbook
      document provided with your delivery. Detailed resolution path visualization is planned for a future release.
    </Typography.Paragraph>
  )
}

const Financials: FC<FinancialsProps> = ({
  totalRevenue,
  unreportedRevenue,
  overviewChartData,
  entityId,
  activityIndex,
  byYear,
  reportedStatus,
}) => {
  const { currentCurrency } = useCurrency()
  const currencyOpts = useMemo(() => ({ currency: currentCurrency }), [currentCurrency])

  return (
    <Flex vertical style={{ gap: 32, fontFamily: 'Roboto' }}>
      <RevenueSummary totalRevenue={totalRevenue} unreportedRevenue={unreportedRevenue} currencyOpts={currencyOpts} />
      <RevenueChart
        overviewChartData={overviewChartData}
        entityId={entityId}
        activityIndex={activityIndex}
        currencyOpts={currencyOpts}
      />
      {byYear && <YearlyBreakdownTable entityId={entityId} byYear={byYear} reportedStatus={reportedStatus} />}
      <Disclaimer />
    </Flex>
  )
}

export default Financials
